'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { isVerifiedFromMetadata } from '@/lib/auth/verification';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  role: 'admin' | 'instructor' | 'student';
  bio?: string;
  phone?: string;
  country?: string;
  is_active: boolean;
  email_verified: boolean;
  is_shs_student: boolean;
  instructor_approved?: boolean;
  created_at: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
  sendVerificationCode: (userId: string, email: string, name: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = useMemo(() => createClient(), []);

  const profileFromUser = (authUser: any): UserProfile | null => {
    if (!authUser) return null;
    const meta = authUser.user_metadata || {};
    const appMeta = authUser.app_metadata || {};
    return {
      id: authUser.id,
      email: authUser.email || '',
      full_name: meta.full_name || '',
      avatar_url: meta.avatar_url,
      role: meta.role || appMeta.role || 'student',
      is_active: true,
      email_verified: isVerifiedFromMetadata(authUser),
      is_shs_student: Boolean(meta.is_shs_student),
      instructor_approved: Boolean(meta.instructor_approved),
      created_at: authUser.created_at,
    };
  };

  // Never calls supabase.auth.* — doing so from inside an onAuthStateChange
  // callback deadlocks on the GoTrue navigator lock. The caller passes the
  // already-known auth user for the metadata fallback instead.
  const fetchProfile = async (userId: string, authUser?: any): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (!error && data) return data as UserProfile;
    } catch {
      // Fall through to metadata fallback
    }

    if (authUser?.id === userId) return profileFromUser(authUser);
    return null;
  };

  useEffect(() => {
    let active = true;

    const loadProfile = (authUser: any) => {
      // Deferred out of the auth callback so the GoTrue lock is released first.
      setTimeout(async () => {
        const p = await fetchProfile(authUser.id, authUser);
        if (active) setProfile(p);
      }, 0);
    };

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) loadProfile(session.user);
      else setProfile(null);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const sendVerificationCode = async (userId: string, email: string, name: string) => {
    // Generate code via DB function (uses service role via RPC)
    const { data: newCode, error: codeError } = await supabase.rpc('create_verification_code', {
      p_user_id: userId,
      p_email: email,
    });

    if (codeError) {
      throw new Error(codeError.message);
    }

    // Send verification email
    await supabase.functions.invoke('send-email', {
      body: {
        type: 'verification',
        to: email,
        name: name || email.split('@')[0],
        code: newCode,
      },
    });
  };

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || metadata?.full_name || '',
          avatar_url: metadata?.avatarUrl || metadata?.avatar_url || '',
          role: metadata?.role || 'student',
          is_shs_student: metadata?.is_shs_student || false,
          is_professional: metadata?.is_professional || false,
          email_verified: false,
        },
        emailRedirectTo: undefined,
      }
    });
    if (error) throw error;

    if (data?.user) {
      // Upsert profile
      try {
        await supabase.from('user_profiles').upsert({
          id: data.user.id,
          email,
          full_name: metadata?.fullName || metadata?.full_name || '',
          role: metadata?.role || 'student',
          is_shs_student: metadata?.is_shs_student || false,
          is_professional: metadata?.is_professional || false,
          email_verified: false,
        }, { onConflict: 'id' });
      } catch {
        // Profile may be created by trigger
      }

      // Generate and send verification code
      try {
        const name = metadata?.fullName || metadata?.full_name || email.split('@')[0];
        await sendVerificationCode(data.user.id, email, name);
      } catch {
        // Non-blocking — user can resend from verification screen
      }
    }

    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    if (data?.user) {
      // Determine role from auth metadata first (reliable, no DB dependency)
      const metaRole =
        data.user.user_metadata?.role ||
        data.user.app_metadata?.role ||
        'student';

      const p = await fetchProfile(data.user.id, data.user);
      setProfile(p);

      const role = p?.role || metaRole;

      // Admins bypass email verification entirely
      if (role === 'admin' || metaRole === 'admin') {
        // Non-blocking login notification
        try {
          await supabase.from('notifications').insert({
            user_id: data.user.id,
            type: 'system',
            title: 'Welcome back, Admin! 👋',
            message: `Admin login at ${new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}`,
            action_url: null,
          });
        } catch {
          // Non-critical
        }
        return data;
      }

      // Keep the session so verification + dashboard redirect can complete.
      const verified =
        p?.email_verified === true || isVerifiedFromMetadata(data.user);
      if (!verified) {
        const verifyError: any = new Error('email_not_verified');
        verifyError.userId = data.user.id;
        verifyError.email = email;
        throw verifyError;
      }

      // Send login notification (non-blocking)
      try {
        await supabase.from('notifications').insert({
          user_id: data.user.id,
          type: 'system',
          title: 'Welcome back! 👋',
          message: `You have successfully logged in to Nerdlora. ${new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}`,
          action_url: null,
        });
      } catch {
        // Non-critical
      }
    }
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return profile?.email_verified === true || isVerifiedFromMetadata(user);
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchProfile(user.id, user);
  };

  const refreshProfile = async () => {
    if (!user) return;
    const p = await fetchProfile(user.id, user);
    setProfile(p);
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
    sendVerificationCode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
